import { formatDistance } from 'date-fns'
import { CheckCircle2, User as UserIcon, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ReferredUser, ReferredUsersStats } from 'types/api'

import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'

interface ReferredUsersListProps {
  users: ReferredUser[]
  stats: ReferredUsersStats
  loading?: boolean
}

const TH =
  'px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground'
const TD = 'px-4 py-3.5 align-middle text-sm text-ink'

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <Card className="p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-xl font-bold text-ink">{value}</div>
    </Card>
  )
}

export function ReferredUsersList({
  users,
  stats,
  loading
}: ReferredUsersListProps) {
  const { t } = useTranslation('settings')

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat
          label={t('referrals.referredUsers.totalUsers')}
          value={stats.total_referred_users}
        />
        <Stat
          label={t('referrals.referredUsers.completedUsers')}
          value={stats.completed_users}
        />
        <Stat
          label={t('referrals.referredUsers.withContracts')}
          value={stats.users_with_contracts}
        />
        <Stat
          label={t('referrals.referredUsers.platformBreakdown')}
          value={`IG: ${stats.platform_breakdown.instagram} / FB: ${stats.platform_breakdown.facebook}`}
        />
      </div>

      <div className="overflow-hidden rounded-2xl border border-ink/[0.06] bg-white">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-ink/[0.08] bg-ink/[0.02] text-left">
                <th className={TH}>{t('referrals.referredUsers.user')}</th>
                <th className={TH}>{t('referrals.referredUsers.source')}</th>
                <th className={TH}>{t('referrals.referredUsers.dateAdded')}</th>
                <th className={TH}>{t('referrals.referredUsers.state')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="px-4 py-10 text-center text-sm text-muted-foreground" colSpan={4}>
                    …
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td className="px-4 py-10 text-center text-sm text-muted-foreground" colSpan={4}>
                    —
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr
                    key={u.user_id}
                    className="border-b border-ink/[0.05] transition-colors last:border-0 hover:bg-ink/[0.015]"
                  >
                    <td className={TD}>
                      <div className="flex items-center gap-3">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-primary">
                          {u.profile_photo ? (
                            <img
                              src={u.profile_photo}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <UserIcon className="h-4 w-4" />
                          )}
                        </span>
                        <div className="min-w-0">
                          <div className="truncate font-medium text-ink">
                            {u.first_name && u.last_name
                              ? `${u.first_name} ${u.last_name}`
                              : u.email}
                          </div>
                          <div className="truncate text-xs text-muted-foreground">
                            {u.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className={TD}>
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
                          u.referral_source === 'Instagram'
                            ? 'bg-[#f3e8ff] text-[#7c3aed]'
                            : 'bg-primary/15 text-primary'
                        )}
                      >
                        {u.referral_source}
                      </span>
                    </td>
                    <td className={cn(TD, 'whitespace-nowrap text-muted-foreground')}>
                      {u.registration_date
                        ? formatDistance(
                            new Date(u.registration_date),
                            new Date(),
                            { addSuffix: true }
                          )
                        : '-'}
                    </td>
                    <td className={TD}>
                      {u.is_active ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-success-soft px-2.5 py-0.5 text-xs font-semibold text-success">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          {t('referrals.referredUsers.active')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-destructive/10 px-2.5 py-0.5 text-xs font-semibold text-destructive">
                          <XCircle className="h-3.5 w-3.5" />
                          {t('referrals.referredUsers.inactive')}
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
